import React from 'react';
import FormLayout from '../FormLayout';
import TextField from './TextField';
import TickettypePricingFieldInput from '../../components/TickettypePricingFieldInput';
import store from '../../redux';
import Utils from '../../services/Utils';

/** FormLayout field: ticket type (+ optional pricing) cascade. */
class TickettypePricingField extends TextField {

    constructor(properties) {
        super({
            ...(TickettypePricingField.getDefaultOptions()),
            ...properties,
        });

        this.type = FormLayout.TICKETTYPE_PRICING;
        this.choices = (this.choices || []).sort((a, b) => a.position - b.position);
    }

    static getDefaultOptions() {
        const properties = super.getDefaultOptions();
        const existing = properties.content && typeof properties.content === 'object'
            ? properties.content
            : {};

        properties.choices = [];
        properties.required = false;
        properties.placeholder = properties.placeholder || {};
        properties.content = {
            ...existing,
            with_pricing: existing.with_pricing !== false,
            tickettype_name: existing.tickettype_name || 'tickettype_id',
            pricing_name: existing.pricing_name || 'pricing_id',
            pricing_choices: Array.isArray(existing.pricing_choices)
                ? existing.pricing_choices
                : [],
            pricing_placeholder: (
                existing.pricing_placeholder
                && typeof existing.pricing_placeholder === 'object'
                && !Array.isArray(existing.pricing_placeholder)
            ) ? existing.pricing_placeholder : {},
        };

        return properties;
    }

    withPricing() {
        return this.content?.with_pricing !== false;
    }

    tickettypeName() {
        const raw = String(this.content?.tickettype_name || 'tickettype_id').trim();
        return raw || 'tickettype_id';
    }

    pricingName() {
        const raw = String(this.content?.pricing_name || 'pricing_id').trim();
        return raw || 'pricing_id';
    }

    pricingAllowlist() {
        return (this.content?.pricing_choices || []).map(String).filter(Boolean);
    }

    /**
     * Resolve pricing ids allowed for a given tickettype.
     * Supports legacy bare ids and scoped `tickettypeId::pricingId` entries.
     *
     * @param {string} tickettypeId
     * @returns {string[]|null} `null` = no filter; array = allowlist
     */
    pricingAllowlistForTickettype(tickettypeId) {
        const raw = this.pricingAllowlist();
        if (!raw.length) {
            return null;
        }
        const tt = String(tickettypeId || '');
        const scoped = [];
        const global = [];
        let hasScoped = false;
        raw.forEach((entry) => {
            const sep = entry.indexOf('::');
            if (sep === -1) {
                global.push(entry);
                return;
            }
            hasScoped = true;
            const entryTt = entry.slice(0, sep);
            const pricingId = entry.slice(sep + 2);
            if (entryTt === tt && pricingId) {
                scoped.push(pricingId);
            }
        });
        if (hasScoped) {
            return [...new Set([...scoped, ...global])];
        }
        return [...new Set(global)];
    }

    availableTickettypeIds() {
        return (this.choices || []).map((choice) => choice.ref_id).filter(Boolean);
    }

    getRefs() {
        const tickettypes = store?.getState()?.tickettypes?.tickettypes || [];
        return tickettypes.map((tickettype) => ({
            id: tickettype._id,
            name: tickettype.name,
        }));
    }

    getRefsSource() {
        return 'tickettypes';
    }

    getHandledOptions() {
        return [
            FormLayout.OPTION_WIDTH,
            FormLayout.OPTION_LABEL,
            FormLayout.OPTION_DESCRIPTION,
            FormLayout.OPTION_REQUIRED,
            FormLayout.OPTION_NAME,
            FormLayout.OPTION_PLACEHOLDER,
            FormLayout.OPTION_CHOICES,
            FormLayout.OPTION_WITH_PRICING,
            FormLayout.OPTION_TICKETTYPE_NAME,
            FormLayout.OPTION_PRICING_NAME,
            FormLayout.OPTION_PRICING_CHOICES,
        ];
    }

    resolveTickettypes(resources) {
        let tickettypes = resources?.tickettypes;
        if (!tickettypes?.length) {
            tickettypes = store?.getState()?.tickettypes?.tickettypes || [];
        }
        return tickettypes || [];
    }

    /**
     * @param {object} [resources]
     * @returns {object[]}
     */
    filteredTickettypes(resources) {
        const allowed = this.availableTickettypeIds();
        const all = this.resolveTickettypes(resources);
        if (!allowed.length) {
            return all;
        }
        return all.filter((tt) => allowed.includes(tt._id));
    }

    getInput(props) {
        const tickettypes = this.filteredTickettypes(props.resources);
        const formName = (props.inEdition || props.inPreview) ? 'formForm' : 'formRenderer';
        const lang = props?.lang;
        const tickettypePlaceholder = Utils.localized_or_fallback(
            this.placeholder,
            lang,
            ''
        );
        const pricingPlaceholder = Utils.localized_or_fallback(
            this.content?.pricing_placeholder,
            lang,
            ''
        );
        return (
            <TickettypePricingFieldInput
                name={this.reduxFieldName(props)}
                formName={formName}
                tickettypes={tickettypes}
                withPricing={this.withPricing()}
                pricingChoices={this.pricingAllowlist()}
                required={this.htmlRequired(props)}
                tickettypePlaceholder={tickettypePlaceholder || undefined}
                pricingPlaceholder={pricingPlaceholder || undefined}
            />
        );
    }

    /**
     * @param {unknown} value
     * @param {{ lang?: string }} context
     * @returns {string}
     */
    renderValue(value, context) {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return super.renderValue(value, context);
        }
        const lang = context?.lang;
        const tickettypes = this.resolveTickettypes(context?.resources);
        const tt = tickettypes.find((t) => t._id === value.tickettype_id);
        const ttLabel = tt
            ? Utils.localized_or_fallback(tt.name, lang, value.tickettype_id)
            : (value.tickettype_id || '');

        if (!this.withPricing() || !value.pricing_id) {
            return ttLabel || '';
        }

        const pricing = tt?.pricings?.[value.pricing_id];
        const pricingLabel = pricing
            ? Utils.localized_or_fallback(pricing.name, lang, value.pricing_id)
            : value.pricing_id;

        if (!ttLabel) {
            return pricingLabel || '';
        }
        if (!pricingLabel) {
            return ttLabel;
        }
        return `${ttLabel} / ${pricingLabel}`;
    }

    getIcon() {
        return 'ticket';
    }
}

export default TickettypePricingField;
